import { onBeforeUnmount } from 'vue';

type RegistryEntry = { id: string; close: () => void };

declare global {
  interface Window {
    __zoneSelectExclusiveOpenRegistries__?: Map<string, Set<RegistryEntry>>;
    __zoneSelectExclusiveOpenSeq__?: number;
  }
}

/** Shared across Vite chunks and separate `createApp` mounts (e.g. team Teammates / Team leads). */
const registries = window.__zoneSelectExclusiveOpenRegistries__ ??= new Map<string, Set<RegistryEntry>>();

function nextInstanceId(): string {
  const seq = (window.__zoneSelectExclusiveOpenSeq__ ??= 0) + 1;
  window.__zoneSelectExclusiveOpenSeq__ = seq;

  return `zone-select-open-${seq}`;
}

function registryFor(groupKey: string): Set<RegistryEntry> {
  let group = registries.get(groupKey);
  if (!group) {
    group = new Set();
    registries.set(groupKey, group);
  }

  return group;
}

/**
 * When several ZoneSelects share a page, opening one should dismiss any other that is already open.
 * Reka treats another select's trigger as outside its layer only after its own dismiss logic runs;
 * coordinating here matches the expected single-open dropdown UX (e.g. project Members roles).
 */
export function useZoneSelectExclusiveOpen(
  enabled: () => boolean,
  close: () => void,
  groupKey = 'default',
) {
  // Not `useId()`: each legacy `createApp` mount resets Vue's id scope, so Teammates and Team leads
  // both came back as `v-0` and `closeOthers` never dismissed the peer.
  const instanceId = nextInstanceId();
  const entry: RegistryEntry = { id: instanceId, close };

  registryFor(groupKey).add(entry);

  onBeforeUnmount(() => {
    registryFor(groupKey).delete(entry);
  });

  function closeOthers() {
    if (!enabled()) {
      return;
    }

    for (const other of registryFor(groupKey)) {
      if (other.id !== instanceId) {
        other.close();
      }
    }
  }

  return { closeOthers };
}
